import { useState } from 'react';
import { Calendar, DollarSign, Clock, User, Upload, FileText } from 'lucide-react';
import {
  useVendorEvents,
  useVendorEventExpenses,
  useVendorEventTimeline,
  useVendorProfile,
  useUpdateVendorProfile,
  useUploadVendorDocument,
} from '../hooks/usePortal';
import type { VendorEvent, VendorProfile } from '../api/portal';

function VendorEventCard({
  event,
  onSelect,
  isSelected,
}: {
  event: VendorEvent;
  onSelect: (event: VendorEvent) => void;
  isSelected: boolean;
}) {
  const dateStr = new Date(event.eventDate).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <button
      onClick={() => onSelect(event)}
      data-testid={`vendor-event-card-${event.eventId}`}
      className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
        isSelected
          ? 'border-primary-500 bg-primary-50'
          : 'border-gray-200 hover:border-gray-300 bg-white'
      }`}
    >
      <h3 className="font-semibold text-lg">{event.eventName}</h3>
      <div className="mt-2 space-y-1 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <Calendar size={14} />
          <span>{dateStr}</span>
        </div>
        {event.eventTypeName && (
          <div>
            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
              {event.eventTypeName}
            </span>
          </div>
        )}
        <div className="flex flex-wrap gap-1 mt-1">
          {event.categories.map((cat) => (
            <span key={cat} className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded">
              {cat}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-4 mt-2">
          <span className="text-xs">
            Quoted: <strong>${event.totalQuoted.toLocaleString()}</strong>
          </span>
          <span className="text-xs">
            Paid: <strong className="text-green-600">${event.totalPaid.toLocaleString()}</strong>
          </span>
        </div>
      </div>
    </button>
  );
}

function VendorExpensesPanel({ eventId }: { eventId: string }) {
  const { data: expenses, isLoading } = useVendorEventExpenses(eventId);

  if (isLoading) return <p className="text-sm text-gray-500">Loading expenses...</p>;
  if (!expenses || expenses.length === 0)
    return <p className="text-sm text-gray-500">No expense items for this event.</p>;

  return (
    <div data-testid="vendor-expenses">
      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
        <DollarSign size={18} />
        Your Expenses
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left p-2">Item</th>
              <th className="text-left p-2">Category</th>
              <th className="text-right p-2">Price</th>
              <th className="text-right p-2">Paid</th>
              <th className="text-right p-2">Balance</th>
              <th className="text-left p-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {expenses.map((item) => (
              <tr key={item.id} className="border-t" data-testid={`expense-row-${item.id}`}>
                <td className="p-2">{item.itemName || '-'}</td>
                <td className="p-2">{item.vendorCategoryName || '-'}</td>
                <td className="p-2 text-right">${item.price.toLocaleString()}</td>
                <td className="p-2 text-right text-green-600">
                  ${item.totalPaid.toLocaleString()}
                </td>
                <td className="p-2 text-right text-red-600">${item.balance.toLocaleString()}</td>
                <td className="p-2">
                  {item.statusName && (
                    <span
                      className="text-xs px-2 py-0.5 rounded"
                      style={{
                        backgroundColor: item.statusColor ? `${item.statusColor}20` : '#f3f4f6',
                        color: item.statusColor || '#374151',
                      }}
                    >
                      {item.statusName}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VendorTimelinePanel({ eventId }: { eventId: string }) {
  const { data: entries, isLoading } = useVendorEventTimeline(eventId);

  if (isLoading) return <p className="text-sm text-gray-500">Loading timeline...</p>;
  if (!entries || entries.length === 0)
    return <p className="text-sm text-gray-500">No timeline entries assigned to you.</p>;

  return (
    <div data-testid="vendor-timeline">
      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
        <Clock size={18} />
        Your Timeline
      </h3>
      <div className="space-y-2">
        {entries.map((entry) => (
          <div
            key={entry.id}
            className="p-3 bg-gray-50 rounded-lg"
            data-testid={`timeline-entry-${entry.id}`}
          >
            <div className="flex items-center gap-2">
              {entry.time && <span className="font-mono text-sm font-bold">{entry.time}</span>}
              <span className="font-medium">{entry.title}</span>
            </div>
            {entry.sectionName && <p className="text-xs text-gray-500 mt-1">{entry.sectionName}</p>}
            {entry.location && (
              <p className="text-xs text-gray-600 mt-1">Location: {entry.location}</p>
            )}
            {entry.vendorNote && (
              <p className="text-xs text-blue-600 mt-1">Note: {entry.vendorNote}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function VendorDocumentUploadPanel({ eventId }: { eventId: string }) {
  const uploadMutation = useUploadVendorDocument();
  const [docType, setDocType] = useState<'quote' | 'invoice'>('quote');
  const [fileName, setFileName] = useState('');
  const [message, setMessage] = useState('');

  function handleUpload() {
    if (!fileName.trim()) return;
    uploadMutation.mutate(
      { eventId, fileName: fileName.trim(), documentType: docType },
      {
        onSuccess: () => {
          setMessage('Document uploaded successfully.');
          setFileName('');
        },
        onError: () => {
          setMessage('Failed to upload document.');
        },
      },
    );
  }

  return (
    <div data-testid="vendor-documents">
      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
        <FileText size={18} />
        Upload Documents
      </h3>
      <div className="space-y-3">
        <div>
          <label htmlFor="vendor-doc-type" className="block text-sm font-medium text-gray-700 mb-1">
            Document Type
          </label>
          <select
            id="vendor-doc-type"
            value={docType}
            onChange={(e) => setDocType(e.target.value as 'quote' | 'invoice')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="quote">Quote</option>
            <option value="invoice">Invoice</option>
          </select>
        </div>
        <div>
          <label
            htmlFor="vendor-doc-filename"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            File Name
          </label>
          <input
            id="vendor-doc-filename"
            type="text"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            placeholder="e.g., quote-march-2026.pdf"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
        </div>
        <button
          onClick={handleUpload}
          disabled={!fileName.trim() || uploadMutation.isPending}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50"
        >
          <Upload size={16} />
          {uploadMutation.isPending ? 'Uploading...' : 'Upload'}
        </button>
        {message && <p className="text-sm text-gray-600">{message}</p>}
      </div>
    </div>
  );
}

function VendorProfilePanel() {
  const { data: profile, isLoading } = useVendorProfile();
  const updateProfile = useUpdateVendorProfile();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Partial<VendorProfile>>({});

  if (isLoading) return <p className="text-sm text-gray-500">Loading profile...</p>;
  if (!profile) return null;

  function startEditing() {
    setForm({
      businessName: profile!.businessName,
      phone: profile!.phone || '',
      email: profile!.email || '',
      website: profile!.website || '',
    });
    setEditing(true);
  }

  function handleSave() {
    updateProfile.mutate(
      {
        businessName: form.businessName,
        phone: form.phone || undefined,
        email: form.email || undefined,
        website: form.website || undefined,
      },
      {
        onSuccess: () => setEditing(false),
      },
    );
  }

  return (
    <div data-testid="vendor-profile" className="bg-white rounded-lg border p-4">
      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
        <User size={18} />
        Business Profile
      </h3>

      {!editing ? (
        <div className="space-y-2 text-sm">
          <p>
            <strong>Business Name:</strong> {profile.businessName}
          </p>
          {profile.phone && (
            <p>
              <strong>Phone:</strong> {profile.phone}
            </p>
          )}
          {profile.email && (
            <p>
              <strong>Email:</strong> {profile.email}
            </p>
          )}
          {profile.website && (
            <p>
              <strong>Website:</strong> {profile.website}
            </p>
          )}
          <button
            onClick={startEditing}
            className="mt-3 px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
          >
            Edit Profile
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <label
              htmlFor="vendor-business-name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Business Name
            </label>
            <input
              id="vendor-business-name"
              type="text"
              value={form.businessName || ''}
              onChange={(e) => setForm((f) => ({ ...f, businessName: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label htmlFor="vendor-phone" className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              id="vendor-phone"
              type="text"
              value={form.phone || ''}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label htmlFor="vendor-email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              id="vendor-email"
              type="email"
              value={form.email || ''}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label
              htmlFor="vendor-website"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Website
            </label>
            <input
              id="vendor-website"
              type="url"
              value={form.website || ''}
              onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={updateProfile.isPending}
              className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {updateProfile.isPending ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={() => setEditing(false)}
              className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function VendorDashboardPage() {
  const { data: events, isLoading } = useVendorEvents();
  const [selectedEvent, setSelectedEvent] = useState<VendorEvent | null>(null);

  if (isLoading) {
    return <p className="p-4 text-gray-500">Loading your events...</p>;
  }

  if (!events || events.length === 0) {
    return (
      <div className="p-8 text-center">
        <h1 className="text-2xl font-bold mb-2">Vendor Dashboard</h1>
        <p className="text-gray-500">You are not assigned to any events yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Vendor Dashboard</h1>

      {/* Vendor Profile */}
      <VendorProfilePanel />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {events.map((event) => (
          <VendorEventCard
            key={event.eventId}
            event={event}
            onSelect={setSelectedEvent}
            isSelected={selectedEvent?.eventId === event.eventId}
          />
        ))}
      </div>

      {selectedEvent && (
        <div className="space-y-4" data-testid="vendor-event-detail">
          <h2 className="text-xl font-semibold">{selectedEvent.eventName}</h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg border p-4">
              <VendorExpensesPanel eventId={selectedEvent.eventId} />
            </div>
            <div className="bg-white rounded-lg border p-4">
              <VendorTimelinePanel eventId={selectedEvent.eventId} />
            </div>
          </div>

          {/* Document Upload */}
          <div className="bg-white rounded-lg border p-4">
            <VendorDocumentUploadPanel eventId={selectedEvent.eventId} />
          </div>
        </div>
      )}
    </div>
  );
}
